/**
 * @module ButtonGroup
 */

import {ClientApplication} from '../../client';
import {getSingleButton} from '../buttonHelper';
import {
  actionWrapper,
  getEventNameSpace,
  getMergedProps,
  sanitizeProps,
  updateActionFromPayload,
  ActionSetWithChildren,
} from '../helper';
import {ActionSetProps, Component, ComponentType, Group, MetaAction} from '../types';
import {getSafeButtonProps, isValidButtonProps, Button, Payload as ButtonPayload} from '../Button';
import {
  invalidActionType,
  invalidPayload,
  throwError,
  ActionType as ErrorActionType,
  ErrorAction,
  Message as ErrorMessage,
} from '../Error';
import {Action, ActionType, Options, Payload} from './types';

export interface ButtonGroupUpdateAction extends MetaAction {
  readonly group: string;
  payload: Payload;
}

export type ButtonGroupAction = ButtonGroupUpdateAction | MetaAction;

export function update(
  group: string,
  component: Component,
  props: Payload,
): ButtonGroupUpdateAction {
  return buttonActionWrapper(group, component, Action.UPDATE, props);
}

export function isGroupedButton(options: ButtonGroup | object): options is ButtonGroup {
  const castOptions = options as ButtonGroup;

  return castOptions.buttons && castOptions.buttons.length > 0 && castOptions.label !== undefined;
}

export function isGroupedButtonPayload(payload: Payload | object): payload is Payload {
  const castOptions = payload as Payload;

  return (
    Array.isArray(castOptions.buttons) &&
    typeof castOptions.id === 'string' &&
    typeof castOptions.label === 'string'
  );
}

export function getSafeButtonGroupProps(props: Payload) {
  return sanitizeProps(props, ['label', 'disabled', 'buttons', 'id']);
}

export function validationError(
  action: ButtonGroupUpdateAction | MetaAction,
): undefined | ErrorAction {
  if (!action.hasOwnProperty('payload')) {
    return invalidPayload(action, ErrorMessage.MISSING_PAYLOAD);
  }
  switch (action.type) {
    case ActionType.UPDATE:
      const payload = (action as ButtonGroupUpdateAction).payload;

      return isValidButtonGroupProps(payload) ? undefined : invalidPayload(action);
    default:
      return invalidActionType(action);
  }
}

export function isValidButtonGroupProps(buttonGroup: Payload) {
  if (
    typeof buttonGroup.id !== 'string' ||
    typeof buttonGroup.label !== 'string' ||
    !Array.isArray(buttonGroup.buttons)
  ) {
    return false;
  }

  for (const button of buttonGroup.buttons) {
    if (!isValidButtonProps(button)) {
      return false;
    }
  }

  return true;
}

export class ButtonGroup extends ActionSetWithChildren implements ActionSetProps<Options, Payload> {
  label!: string;
  disabled = false;
  buttonsOptions: Button[] = [];
  buttons: ButtonPayload[] = [];
  constructor(app: ClientApplication<any>, options: Options) {
    super(app, ComponentType.ButtonGroup, Group.ButtonGroup);
    this.set(options, false);
  }

  get options(): Options {
    return {
      buttons: this.buttonsOptions,
      disabled: this.disabled,
      label: this.label,
    };
  }

  get payload(): Payload {
    return {
      ...this.options,
      buttons: this.buttons,
      id: this.id,
    };
  }

  set(options: Partial<Options>, shouldUpdate = true) {
    const mergedOptions = getMergedProps(this.options, options);
    const {label, disabled, buttons} = mergedOptions;

    if (!this.validateOptions(mergedOptions)) {
      return this;
    }

    this.label = label;
    this.disabled = !!disabled;
    this.buttons = this.getButtons(buttons);
    if (shouldUpdate) {
      this.dispatch(Action.UPDATE);
    }

    return this;
  }

  dispatch(action: Action) {
    switch (action) {
      case Action.UPDATE:
        const updateAction = update(this.group, this.component, this.payload);
        this.app.dispatch(updateAction);
        break;
      default:
        throwError(
          ErrorActionType.INVALID_ACTION_TYPE,
          actionWrapper({
            group: this.group,
            type: action,
          }),
        );
    }

    return this;
  }

  updateButtons(newPayload: ButtonPayload) {
    if (!this.buttons || this.buttons.length === 0) {
      return;
    }
    let updated;
    for (const action of this.buttons) {
      updated = updateActionFromPayload(action, getSafeButtonProps(newPayload));
      if (updated) {
        break;
      }
    }
    if (updated) {
      this.dispatch(Action.UPDATE);
    }
  }

  protected getSingleButton(button: Button): ButtonPayload {
    return getSingleButton(this, button, this.subgroups, this.updateButtons);
  }

  protected getButtons(buttonOptions?: Button[]): ButtonPayload[] {
    const buttons: ButtonPayload[] = [];
    if (!buttonOptions) {
      return [];
    }

    buttonOptions.forEach((button: Button) => {
      const singleButton = getSingleButton(this, button, this.subgroups, this.updateButtons);
      buttons.push(singleButton);
    });
    this.buttonsOptions = buttonOptions;

    return buttons;
  }

  private validateOptions(options: Options) {
    const buttonGroupProps = {id: this.id, ...options};
    if (!isValidButtonGroupProps(buttonGroupProps)) {
      const errorAction = update(this.group, this.component, buttonGroupProps);
      throwError(ErrorActionType.INVALID_OPTIONS, errorAction);

      return false;
    }

    return true;
  }
}

export function create(app: ClientApplication<any>, options: Options) {
  return new ButtonGroup(app, options);
}

function buttonActionWrapper(
  group: string,
  component: Component,
  eventName: string,
  props: Payload,
  payload?: any,
): any {
  const {id} = component;
  const {label} = props;
  const action = getEventNameSpace(group, eventName, component);
  const buttonPayload = {
    id,
    label,
    ...getSafeButtonGroupProps(props),
    payload,
  };

  return actionWrapper({type: action, group, payload: buttonPayload});
}



// WEBPACK FOOTER //
// ./src/actions/ButtonGroup/actions.ts